import ApiClient from '../ApiClient';
import { ApplePayService } from './service';
import { ApplePayCombinedShippingOptions, ApplePayPageType, ValidateMerchantResponse } from './types';
import DigitalWalletsBase from '../DigitalWalletsBase/DigitalWalletsBase';
import {
  DigitalWalletSuborderShippings,
  DigitalWalletShippingMethod,
  DigitalWalletsShippingMethods,
} from '../DigitalWalletsBase/types';
import { CheckoutOrderSummary } from '@package/core/src/checkout/types';
import { ShoppingCard } from '@package/core/src/shoppingCart/types';
import { ShippingOptionsData } from '@package/core/src/shipping/types';
import {
  getSelectedShippingOptions,
  SelectedShippingOption as SelectedShippingOptionType,
} from '@package/utils/src/shippingUtils/getSelectedShippingOptions';

class ApplePayServiceImpl extends DigitalWalletsBase implements ApplePayService {
  public constructor(protected apiClient: ApiClient) {
    super(apiClient);
  }

  public getLineItems(orderSummary: CheckoutOrderSummary, page: ApplePayPageType): ApplePayJS.ApplePayLineItem[] {
    const lineItems: ApplePayJS.ApplePayLineItem[] = [];

    // Products & Services
    if (orderSummary.subTotal) {
      lineItems.push({
        label: 'Products & Services',
        amount: String(orderSummary.subTotal),
      });
    }

    // Additional Product Fees
    orderSummary.additionalProductFees?.forEach((productFee) => {
      if (productFee.price) {
        lineItems.push({
          label: productFee.name || 'Additional Product Fee',
          amount: String(productFee.price),
        });
      }
    });

    // Coupon Discount
    if (orderSummary.couponDiscount) {
      lineItems.push({
        label: 'Coupon Discount',
        amount: `-${String(orderSummary.couponDiscount)}`,
      });
    }

    // Auto Apply Coupon
    if (orderSummary.autoApplyCouponResponse?.autoCouponDiscount) {
      lineItems.push({
        label: orderSummary.autoApplyCouponResponse.couponNumber,
        amount: `-${String(orderSummary.autoApplyCouponResponse.autoCouponDiscount)}`,
      });
    }

    // Sales Tax
    if (orderSummary.tax && orderSummary.tax > 0) {
      lineItems.push({
        label:
          page === 'LOGIN'
            ? 'Estimated Taxes'
            : orderSummary.country === 'Canada'
            ? 'Handling Fee'
            : orderSummary.state
            ? `${orderSummary.state} Sales Tax`
            : 'Sales Tax',
        amount: String(orderSummary.tax),
      });
    }

    // Shipping
    if (orderSummary.shipping) {
      lineItems.push({
        label: 'Shipping',
        amount: String(orderSummary.shipping),
      });
    }

    // Gift Card Payments
    if (orderSummary.giftCard) {
      lineItems.push({
        label: 'Gift Card Payments',
        amount: `-${String(orderSummary.giftCard)}`,
      });
    }

    // Bundle Discount
    if (orderSummary.bundleDiscount) {
      lineItems.push({
        label: 'Bundle Discount',
        amount: `-${String(orderSummary.bundleDiscount)}`,
      });
    }

    // Signature Required
    if (orderSummary.signatureRequired) {
      lineItems.push({
        label: 'Signature Required',
        amount: String(orderSummary.signatureRequired),
      });
    }

    // Wire Transfer Fee
    if (orderSummary.wireTransferFee) {
      lineItems.push({
        label: 'Wire Transfer Fee',
        amount: String(orderSummary.wireTransferFee),
      });
    }

    // Bullseye Bucks
    if (orderSummary.bullseyeBucks) {
      lineItems.push({
        label: 'Bullseye Bucks',
        amount: `-${String(orderSummary.bullseyeBucks)}`,
      });
    }

    return lineItems;
  }

  public async getApplePaySession(validationUrl: string): Promise<ValidateMerchantResponse> {
    const res = await this.apiClient.post('/shoppingCart/paymentMethod/digital-wallet/apple-pay/verify-merchant', {
      body: { validationUrl },
    });
    return res.json();
  }

  public async getCombinedShippingOptions(
    country: string,
    state: string,
    city: string,
    zipcode: string,
  ): Promise<ApplePayCombinedShippingOptions> {
    const combinedShippingOptions = await this.getDigitalWalletsShippingMethods(country, state, city, zipcode);

    const applePayShippingMethods =
      combinedShippingOptions.shipments.length > 1
        ? this.createCombinedShippingMethods(combinedShippingOptions)
        : this.createSingleShippingMethods(combinedShippingOptions);

    return {
      ...combinedShippingOptions,
      applePayShippingMethods,
    };
  }

  private formatShippingLabel(
    selectedShippingOption: Array<SelectedShippingOptionType>,
    methodName: string,
    amount: string,
    isMultipleSuborders: boolean,
  ): string {
    if (!selectedShippingOption || (Array.isArray(selectedShippingOption) && selectedShippingOption.length === 0)) {
      return `Total Amount ${amount}`;
    }
    // Handle array inputs (both single and multiple suborders)
    if (Array.isArray(selectedShippingOption) && selectedShippingOption.length > 0) {
      if (isMultipleSuborders) {
        const latestDeliveryOption = selectedShippingOption.reduce((latest, option) => {
          const dateStr = option.latestDeliveryDate;
          return !latest || (dateStr && dateStr > latest.latestDeliveryDate) ? option : latest;
        }, selectedShippingOption[0]);
        return `Estimated Delivery: ${
          latestDeliveryOption.deliveryTime || latestDeliveryOption.deliveryDate
        } - ${amount}`;
      }

      // For single shipping option
      const singleOption = selectedShippingOption[0];
      let dateStr = singleOption.latestDeliveryDate.split('-').join('/');
      const date = new Date(dateStr);

      return !isNaN(date.getTime())
        ? `Get it ${selectedShippingOption[0].deliveryDate} | ${methodName}, ${amount}`
        : `${methodName}, ${amount}`;
    }
    const date = new Date(selectedShippingOption[0].latestDeliveryDate);

    if (isNaN(date.getTime())) {
      console.error('Invalid date:', selectedShippingOption[0].latestDeliveryDate);
      return `${methodName}, ${amount}`;
    }

    return `Get it ${selectedShippingOption[0].deliveryDate} | ${methodName}, ${amount}`;
  }

  private createCombinedShippingMethods(data: DigitalWalletsShippingMethods): ApplePayJS.ApplePayShippingMethod[] {
    const cheapestOptions = this.findCheapestOptions(data.shipments);
    const expensiveOptions = this.findExpensiveOptions(data.shipments);

    const cheapestTotal = this.calculateTotal(cheapestOptions);
    const expensiveTotal = this.calculateTotal(expensiveOptions);
    const cheapestDescription = this.getLatestDeliveryDate(cheapestOptions);
    const expensiveDescription = this.getLatestDeliveryDate(expensiveOptions);

    return [
      {
        label: `Save money and deliver all shipments by:`,
        detail: cheapestDescription,
        amount: `${cheapestTotal}`,
        identifier: this.createIdentifier(cheapestOptions, data.shipments),
      },
      {
        label: `Rush all of my shipments and deliver everything by:`,
        detail: expensiveDescription,
        amount: `${expensiveTotal}`,
        identifier: this.createIdentifier(expensiveOptions, data.shipments),
      },
    ];
  }

  public getTotalLabel(
    cartData: ShoppingCard | null,
    shippingOptions: ShippingOptionsData | null,
    isMultipleSuborders: boolean,
  ): string {
    if (!cartData || !shippingOptions) return '';

    const selectedOptions = getSelectedShippingOptions(shippingOptions, cartData.subOrders);
    const shippingTotal = cartData.notDeliverable
      ? cartData.cartSummary?.totalDue
      : selectedOptions.reduce((sum, option) => {
          const optionPrice = 'price' in option ? option.price : 'sum' in option ? option.sum : 0;
          return sum + (optionPrice || 0);
        }, 0);
    return this.formatShippingLabel(
      selectedOptions,
      selectedOptions[0]?.name || '',
      shippingTotal === 0 ? 'FREE' : `$${shippingTotal.toFixed(2)}`,
      isMultipleSuborders,
    );
  }

  private createSingleShippingMethods(data: DigitalWalletsShippingMethods): ApplePayJS.ApplePayShippingMethod[] {
    return data.shipments.flatMap((shipment: DigitalWalletSuborderShippings) =>
      shipment.shippingOptions.map((option: DigitalWalletShippingMethod) => ({
        label: option.label,
        detail: option.deliveryDate ? `${option.deliveryDate}` : '',
        amount: `${option.amount}`,
        identifier: `${shipment.subOrderId}-${option.methodId}`,
      })),
    );
  }

  private formatSingleDateFromDate(dateInput: any): { dayOfWeek: string; formattedDate: string } | null {
    if (!dateInput) return null;

    const date = new Date(dateInput);
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateInput);
      return null;
    }

    const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });
    const formattedDate = date
      .toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
      })
      .replace(/\//g, '-');

    return { dayOfWeek, formattedDate };
  }
}

export default ApplePayServiceImpl;
